import React from 'react';
import SelectField from '../common/SelectField.js';
import { ArrowLeft } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import { ValidationMessage } from '../common/Validation.js';
import { useTranslation } from 'react-i18next';


const inputClass = (hasError) =>
  `w-full min-w-0 px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;

const labelClass = 'block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2';

const ContractPartyForm = ({
  title,
  icon: Icon,
  formData,
  errors,
  onChange,
  onSubmit,
  onCancel,
  loading,
  submitLabel,
  loadingLabel,
}) => {
  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex items-center gap-2 sm:gap-3">
          <Icon className="w-6 h-6 text-buttonhoverTxt shrink-0" />
          {title}
        </h1>
        <Button variant="secondary" icon={ArrowLeft} onClick={onCancel} className="w-full sm:w-auto">
          Back to List
        </Button>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 lg:p-8"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-5">
              <div className="min-w-0">
                <label className={labelClass}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={onChange}
                  placeholder="Enter contract party name"
                  className={inputClass(errors.name)}
                />
                <ValidationMessage error={errors.name} />
              </div>

              <div className="min-w-0">
                <label className={labelClass}>
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contact"
                  inputMode="numeric"
                  value={formData.contact}
                  onChange={onChange}
                  placeholder="Enter 10-digit number"
                  maxLength={10}
                  className={inputClass(errors.contact)}
                />
                <ValidationMessage error={errors.contact} />
              </div>

              <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                <label className={labelClass}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={onChange}
                  placeholder="Enter email address"
                  className={inputClass(errors.email)}
                />
                <ValidationMessage error={errors.email} />
              </div>

              <div className="min-w-0">
                <label className={labelClass}>
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={onChange}
                  placeholder="Enter company name"
                  className={inputClass(errors.company_name)}
                />
                <ValidationMessage error={errors.company_name} />
              </div>

              <div className="min-w-0">
                <label className={labelClass}>GST Number</label>
                <input
                  type="text"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={onChange}
                  placeholder="Enter GST number (optional)"
                  className={inputClass(errors.gst_number)}
                />
                <ValidationMessage error={errors.gst_number} />
              </div>

              <div className="min-w-0">
                <label className={labelClass}>Status</label>
                <SelectField variant="form"
                  name="status"
                  value={formData.status}
                  onChange={onChange}
                  className="w-full min-w-0 px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base font-medium text-gray-700"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </SelectField>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
              Address
            </h3>
            <div className="min-w-0">
              <label className={labelClass}>
                Full Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={onChange}
                placeholder="Enter complete address"
                rows={2}
                className={`${inputClass(errors.address)} resize-none`}
              />
              <ValidationMessage error={errors.address} />
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-6 pt-5 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onCancel} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={loading} disabled={loading} className="w-full sm:w-auto">
            {loading ? loadingLabel : submitLabel}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
};

export default ContractPartyForm;
